import { useState, useEffect, useMemo } from "react";

import {
  Modal,
  Tabs,
  Tab,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import ImagePlaceholder from "components/Image/ImagePlaceholder";
import MaskAndValid from "utils/maskAndValid";
import themeColorsDesingSystem from "assets/theme/ds-colors";

import AuditTab from "./audit-tab";

const PRODUCTS_PAGE_SIZE = 10;
const ACCENT_BLUE = "#1E88E5";

const labelSx = {
  fontSize: "0.7rem",
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: themeColorsDesingSystem.gray.main,
  mb: 0.5,
};

const valueSx = {
  fontSize: "0.95rem",
  fontWeight: 700,
  color: themeColorsDesingSystem.primary.main,
  lineHeight: 1.4,
};

function TabPanel({ children, value, index }) {
  if (value !== index) return null;
  return <Box role="tabpanel">{children}</Box>;
}

function LoadingData() {
  return (
    <Box display="flex" justifyContent="center" alignItems="center" py={6}>
      <CircularProgress size={28} />
    </Box>
  );
}

function DetailField({ label, children, highlight }) {
  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      <Typography sx={labelSx}>{label}</Typography>
      {typeof children === "string" || typeof children === "number" ? (
        <Typography
          sx={{
            ...valueSx,
            color: highlight ? ACCENT_BLUE : valueSx.color,
            wordBreak: "break-word",
          }}
        >
          {children}
        </Typography>
      ) : (
        children
      )}
    </Box>
  );
}

function StatusBadge({ status }) {
  if (!status) return "—";
  const normalized = String(status).toUpperCase();
  const isCadastrado = normalized === "CADASTRADO";
  const isPublicado = normalized === "PUBLICADO";

  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        px: 1.5,
        py: 0.35,
        borderRadius: "4px",
        fontSize: "0.75rem",
        fontWeight: 700,
        letterSpacing: "0.04em",
        bgcolor: isCadastrado
          ? themeColorsDesingSystem.green.background
          : isPublicado
            ? themeColorsDesingSystem.secondary.active
            : themeColorsDesingSystem.stone.light,
        color: isCadastrado
          ? themeColorsDesingSystem.green.main
          : isPublicado
            ? themeColorsDesingSystem.primary.main
            : themeColorsDesingSystem.gray.dark,
      }}
    >
      {normalized}
    </Box>
  );
}

function formatCurrency(value) {
  if (value == null || value === "") return "—";
  return `R$ ${MaskAndValid.maskPrice(value)}`;
}

function getPaymentConditionsList(showcaseData) {
  if (showcaseData?.CondicoesPagamento?.length > 0) {
    return showcaseData.CondicoesPagamento;
  }
  if (showcaseData?.CondicaoPagamento) {
    return [showcaseData.CondicaoPagamento];
  }
  return [];
}

function getPriceTablesList(showcaseData) {
  if (showcaseData?.TabelasPreco?.length > 0) {
    return showcaseData.TabelasPreco;
  }
  const codes = showcaseData?.CodigosTabelaPreco;
  if (codes?.length > 0) {
    return codes.map((cod) => ({ CodigoTabelaPreco: cod }));
  }
  if (showcaseData?.CodigoTabelaPreco != null) {
    return [{ CodigoTabelaPreco: showcaseData.CodigoTabelaPreco }];
  }
  return [];
}

function formatPaymentConditionLine(condition) {
  if (!condition) return null;
  const politica =
    condition.Politica?.trim() ||
    (condition.IdCondicaoPagamento != null
      ? `Condição ${condition.IdCondicaoPagamento}`
      : null);
  if (!politica) return null;
  const pct = condition.PercentualDesconto;
  if (pct != null && pct !== "") {
    return `${politica} · ${pct}%`;
  }
  return politica;
}

function formatPriceTableLine(table) {
  if (!table) return null;
  const cod = table.CodigoTabelaPreco ?? table.CodTabelaPreco;
  if (cod == null || cod === "") return null;
  const desc = (table.Descricao ?? table.Nome ?? "").trim();
  return desc ? `${cod} · ${desc}` : String(cod);
}

function formatDiscountLines(showcaseData) {
  const conditions = getPaymentConditionsList(showcaseData);
  const lines = conditions
    .map((c) => {
      const pct = c?.PercentualDesconto;
      if (pct == null || pct === "") return null;
      const politica =
        c.Politica?.trim() ||
        (c.IdCondicaoPagamento != null
          ? `Condição ${c.IdCondicaoPagamento}`
          : "Desconto");
      return `${politica}: ${pct}%`;
    })
    .filter(Boolean);
  return lines.length > 0 ? lines : null;
}

function MultiValueLines({ lines }) {
  if (!lines?.length) {
    return "—";
  }
  if (lines.length === 1) {
    return lines[0];
  }
  return (
    <Box
      component="span"
      sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}
    >
      {lines.map((line) => (
        <Typography
          key={line}
          component="span"
          sx={{ ...valueSx, display: "block" }}
        >
          {line}
        </Typography>
      ))}
    </Box>
  );
}

function GeneralTab({ showcaseData }) {
  const vigencia = showcaseData?.FinalizarEstoque
    ? "Até finalizar o estoque"
    : showcaseData?.DataVigenciaInicioFormat ||
        showcaseData?.DataVigenciaFimFormat
      ? `${showcaseData?.DataVigenciaInicioFormat || "—"} - ${
          showcaseData?.DataVigenciaFimFormat || "—"
        }`
      : "—";

  const columnSx = {
    display: "flex",
    flexDirection: "column",
    gap: 2.5,
    minWidth: 0,
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) minmax(0, 1fr)" },
        columnGap: { xs: 0, sm: 5 },
        rowGap: { xs: 2.5, sm: 0 },
        width: "100%",
      }}
    >
      <Box sx={columnSx}>
        <DetailField label="Nome da Vitrine">
          {showcaseData?.Nome || "—"}
        </DetailField>
        <DetailField label="Descrição">
          {showcaseData?.Descricao || "—"}
        </DetailField>
        <DetailField label="Status">
          <StatusBadge status={showcaseData?.Status} />
        </DetailField>
        <DetailField label="Data de Criação">
          {showcaseData?.DataInclusaoFormat || "—"}
        </DetailField>
        <DetailField label="Vigência">{vigencia}</DetailField>
      </Box>

      <Box sx={columnSx}>
        <DetailField label="Criada por">
          {showcaseData?.NomeUsuario || "—"}
        </DetailField>
        <DetailField
          label={
            getPriceTablesList(showcaseData).length > 1
              ? "Tabelas de Preço"
              : "Tabela de Preço"
          }
        >
          <MultiValueLines
            lines={getPriceTablesList(showcaseData)
              .map(formatPriceTableLine)
              .filter(Boolean)}
          />
        </DetailField>
        <DetailField
          label={
            getPaymentConditionsList(showcaseData).length > 1
              ? "Condições de Pagamento"
              : "Condição de Pagamento"
          }
        >
          <MultiValueLines
            lines={getPaymentConditionsList(showcaseData)
              .map(formatPaymentConditionLine)
              .filter(Boolean)}
          />
        </DetailField>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2.5,
            width: "100%",
          }}
        >
          <DetailField label="Quantidade mínima" highlight>
            {showcaseData?.QuantidadeMinimaPedido}
          </DetailField>
          <DetailField label="Valor Mínimo">
            {formatCurrency(showcaseData?.ValorMinimo)}
          </DetailField>
        </Box>
      </Box>
    </Box>
  );
}

function EntityList({ items, getName, getDocument, getKey, badgeColor }) {
  if (!items?.length) {
    return (
      <Typography color="textSecondary" sx={{ py: 2 }}>
        Nenhum registro encontrado.
      </Typography>
    );
  }

  return (
    <Box>
      {items.map((item, index) => (
        <Box
          key={getKey?.(item) ?? index}
          display="flex"
          alignItems="center"
          gap={2}
          py={2}
          sx={{
            borderBottom:
              index < items.length - 1
                ? `1px solid ${themeColorsDesingSystem.stone.main}`
                : "none",
          }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              bgcolor: badgeColor,
              color: themeColorsDesingSystem.primary.main,
              fontWeight: 700,
              fontSize: "0.85rem",
            }}
          >
            {index + 1}
          </Box>
          <Box minWidth={0}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "0.9rem",
                color: themeColorsDesingSystem.primary.main,
                textTransform: "uppercase",
                lineHeight: 1.3,
              }}
            >
              {getName(item)}
            </Typography>
            <Typography
              sx={{
                fontSize: "0.8rem",
                color: themeColorsDesingSystem.gray.main,
                mt: 0.25,
              }}
            >
              {getDocument(item)}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function ProductsTab({ items }) {
  const [page, setPage] = useState(0);
  const products = items ?? [];

  useEffect(() => {
    setPage(0);
  }, [products.length]);

  const totalPages = Math.max(
    1,
    Math.ceil(products.length / PRODUCTS_PAGE_SIZE),
  );
  const safePage = Math.min(page, totalPages - 1);
  const pageItems = useMemo(() => {
    const start = safePage * PRODUCTS_PAGE_SIZE;
    return products.slice(start, start + PRODUCTS_PAGE_SIZE);
  }, [products, safePage]);

  const pageNumbers = useMemo(() => {
    const maxButtons = 5;
    if (totalPages <= maxButtons) {
      return Array.from({ length: totalPages }, (_, i) => i);
    }
    let start = Math.max(0, safePage - 2);
    if (start + maxButtons > totalPages) {
      start = totalPages - maxButtons;
    }
    return Array.from({ length: maxButtons }, (_, i) => start + i);
  }, [totalPages, safePage]);

  if (!products.length) {
    return (
      <Typography color="textSecondary" sx={{ py: 2 }}>
        Nenhum produto vinculado à vitrine.
      </Typography>
    );
  }

  const showingFrom = safePage * PRODUCTS_PAGE_SIZE + 1;
  const showingTo = Math.min(
    (safePage + 1) * PRODUCTS_PAGE_SIZE,
    products.length,
  );

  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {[
                "Imagem",
                "Cód. Produto",
                "Nome Comercial",
                "Ativo",
                "Preço",
              ].map((header) => (
                <TableCell
                  key={header}
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    color: themeColorsDesingSystem.gray.main,
                    borderBottom: `1px solid ${themeColorsDesingSystem.stone.main}`,
                    py: 1.5,
                  }}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {pageItems.map((row) => {
              const ativoLabel = MaskAndValid.formatYesNo(row?.Ativo);
              return (
                <TableRow
                  key={row?.IdProduto ?? row?.Codigo}
                  sx={{
                    "&:last-child td": { borderBottom: 0 },
                    "& td": {
                      borderBottom: `1px solid ${themeColorsDesingSystem.stone.light}`,
                      py: 1.5,
                    },
                  }}
                >
                  <TableCell width={72}>
                    <ImagePlaceholder
                      src={row?.Foto}
                      alt={`Imagem do produto ${row?.IdProduto}`}
                      width="48px"
                      height="48px"
                      widthOnError="48px"
                      heightOnError="48px"
                    />
                  </TableCell>
                  <TableCell sx={{ ...valueSx, fontWeight: 600 }}>
                    {row?.IdProduto ?? "—"}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: themeColorsDesingSystem.primary.main,
                      maxWidth: 320,
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                    }}
                  >
                    {row?.DescricaoProduto ?? "—"}
                  </TableCell>
                  <TableCell>
                    <Typography
                      component="span"
                      sx={{
                        fontWeight: 600,
                        color:
                          row?.Ativo === true || row?.Ativo === "true"
                            ? themeColorsDesingSystem.green.main
                            : themeColorsDesingSystem.gray.dark,
                      }}
                    >
                      {ativoLabel}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ ...valueSx, whiteSpace: "nowrap" }}>
                    {row?.Preco != null
                      ? `R$ ${MaskAndValid.maskPrice(row.Preco)}`
                      : "—"}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        mt={2}
        pt={1}
      >
        <Typography
          variant="body2"
          sx={{ color: themeColorsDesingSystem.gray.main }}
        >
          Mostrando {showingFrom}–{showingTo} de {products.length} registros
        </Typography>
        <Box display="flex" alignItems="center" gap={0.5}>
          <IconButton
            size="small"
            disabled={safePage === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            aria-label="página anterior"
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          {pageNumbers.map((pageIndex) => (
            <Box
              key={pageIndex}
              component="button"
              type="button"
              onClick={() => setPage(pageIndex)}
              sx={{
                minWidth: 32,
                height: 32,
                border:
                  pageIndex === safePage
                    ? "none"
                    : `1px solid ${themeColorsDesingSystem.stone.main}`,
                borderRadius: "4px",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: "0.85rem",
                bgcolor:
                  pageIndex === safePage
                    ? themeColorsDesingSystem.primary.main
                    : themeColorsDesingSystem.white.main,
                color:
                  pageIndex === safePage
                    ? themeColorsDesingSystem.white.main
                    : themeColorsDesingSystem.primary.main,
              }}
            >
              {pageIndex + 1}
            </Box>
          ))}
          <IconButton
            size="small"
            disabled={safePage >= totalPages - 1}
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            aria-label="próxima página"
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </>
  );
}

const contentBorderSx = {
  border: `1px solid ${themeColorsDesingSystem.stone.main}`,
  borderRadius: "8px",
  p: { xs: 2, sm: 3 },
  minHeight: 280,
};

export default function DetailsModal({
  open,
  onClose,
  showcaseData,
  loadingDetails,
}) {
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (!open) {
      setActiveTab(0);
    }
  }, [open]);

  const uniqueClientes = showcaseData?.Clientes?.filter(
    (cliente, index, self) =>
      index === self.findIndex((c) => c.IdPessoaLoja === cliente.IdPessoaLoja),
  );

  const uniqueRepresentantes = showcaseData?.Representantes?.filter(
    (representante, index, self) =>
      index ===
      self.findIndex(
        (r) => r.IdPessoaRepresentante === representante.IdPessoaRepresentante,
      ),
  );

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        p: 2,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 960,
          maxHeight: "90vh",
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: 24,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          outline: "none",
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          px={3}
          pt={2.5}
          pb={1}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: themeColorsDesingSystem.primary.main,
            }}
          >
            {showcaseData?.Nome || "Visualização da Vitrine"}
          </Typography>
          <IconButton onClick={onClose} aria-label="fechar" size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            px: 3,
            minHeight: 44,
            borderBottom: `1px solid ${themeColorsDesingSystem.stone.main}`,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.9rem",
              minHeight: 44,
              color: themeColorsDesingSystem.gray.main,
              "&.Mui-selected": {
                color: themeColorsDesingSystem.primary.main,
              },
            },
            "& .MuiTabs-indicator": {
              height: 3,
              backgroundColor: themeColorsDesingSystem.primary.main,
            },
          }}
        >
          <Tab label="Dados Gerais" />
          <Tab label="Produtos" />
          <Tab label="Representantes" />
          <Tab
            label="Clientes"
            disabled={showcaseData?.Status !== "PUBLICADO"}
          />
          <Tab label="Auditoria" />
        </Tabs>

        <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 2.5 }}>
          <Box sx={contentBorderSx}>
            {loadingDetails ? (
              <LoadingData />
            ) : (
              <>
                <TabPanel value={activeTab} index={0}>
                  <GeneralTab showcaseData={showcaseData} />
                </TabPanel>
                <TabPanel value={activeTab} index={1}>
                  <ProductsTab items={showcaseData?.Itens} />
                </TabPanel>
                <TabPanel value={activeTab} index={2}>
                  <EntityList
                    items={uniqueRepresentantes}
                    badgeColor={themeColorsDesingSystem.secondary.active}
                    getKey={(r) => r?.IdPessoaRepresentante}
                    getName={(r) => r?.NomeRepresentante}
                    getDocument={(r) =>
                      MaskAndValid.maskCNPJ(r?.CnpjCpfRepresentante)
                    }
                  />
                </TabPanel>
                <TabPanel value={activeTab} index={3}>
                  <EntityList
                    items={uniqueClientes}
                    badgeColor={themeColorsDesingSystem.green.background}
                    getKey={(c) => c?.IdPessoaLoja}
                    getName={(c) => c?.NomeLoja}
                    getDocument={(c) => MaskAndValid.maskCNPJ(c?.CnpjCpfLoja)}
                  />
                </TabPanel>
                <TabPanel value={activeTab} index={4}>
                  <AuditTab
                    vitrineId={showcaseData?.IdVitrineVirtualB2b}
                    active={activeTab === 4}
                  />
                </TabPanel>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}
